import { useState, useCallback } from 'react';
import { ALL_PRESETS } from '@/config/presetConfig';
import { ambientSoundPresets } from '@/config/audioConfig';
import type { ChatMessage } from '@/api/types/llm';

interface CustomSession {
  frequency: number;
  duration: number;
  ambientSounds?: string[];
  soundscapes?: string[];
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  sessionSuggestion?: {
    presetId?: string;
    customSession?: {
      frequency: number;
      duration: number;
      ambientSounds: string[];
      soundscapes: string[];
    };
  };
}

const SYSTEM_PROMPT = `You are an AI assistant specialized in brainwave frequencies, meditation, and focus enhancement. Your expertise includes:

1. Brainwave Frequencies:
   - Delta (0.5-4 Hz): Deep sleep, healing, regeneration
   - Theta (4-8 Hz): Light sleep, meditation, creativity
   - Alpha (8-12 Hz): Relaxation, stress reduction, learning
   - Beta (12-30 Hz): Focus, concentration, active thinking
   - Gamma (30-100 Hz): Peak performance, insight, problem-solving

2. Available Presets (use these exact IDs):
   ${ALL_PRESETS.map(preset => `   - ${preset.id}: ${preset.name} (${preset.frequency} Hz) - ${preset.description}`).join('\n')}

3. Available Ambient Sounds:
   ${ambientSoundPresets.map(sound => `   - ${sound.id}: ${sound.name} (${sound.category})`).join('\n')}

4. Response Rules:
   - For preset suggestions: Include a JSON structure with presetId using the exact IDs listed above
   - For custom sessions: Include a JSON structure with frequency, duration, and sound settings
   - Keep responses concise and focused on the user's specific needs
   - Use markdown formatting for better readability
   - Always explain the benefits of the suggested session

5. Interactive Session Recommendation:
   When users ask for a session recommendation, follow these steps:
   1. Ask about their current state (energy level, stress, focus, etc.)
   2. Ask about their goals (productivity, relaxation, sleep, etc.)
   3. Ask about their experience level with meditation/binaural beats
   4. Ask about their preferred session duration
   5. Based on their responses, suggest either a preset or custom session
   6. Explain why the suggested session would be beneficial for their needs

6. Off-topic Handling:
   - If a question is off-topic, respond with: "I can only help with brainwave frequencies, meditation, focus enhancement, and related topics. How can I assist you with those areas?"
   - Do not provide medical advice or make health claims

Example response format for session suggestions:
\`\`\`json
{
  "type": "session_suggestion",
  "data": {
    "presetId": "focus" // or
    "customSession": {
      "frequency": 15,
      "duration": 30,
      "ambientSounds": ["rain", "white-noise"],
      "soundscapes": ["atmospheric"],
      "advancedSettings": {
        "carrierFrequency": 300,
        "leftWaveform": "sine",
        "rightWaveform": "sine"
      }
    }
  }
}
\`\`\`

Remember to maintain a supportive and encouraging tone while providing accurate, scientifically-based information.`;

const DEFAULT_GREETING = `Hello! I'm your Brainwave Assistant. I can help you with:

• Finding the perfect session for your needs
• Understanding different brainwave frequencies
• Creating custom sessions with specific settings
• Tips for meditation and focus
• Recommendations for sleep and relaxation

What would you like to know about?`;

export const useChat = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: DEFAULT_GREETING,
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const parseSessionSuggestion = (content: string) => {
    try {
      const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch) {
        const jsonStr = jsonMatch[1];
        const data = JSON.parse(jsonStr);
        if (data.type === 'session_suggestion') {
          // Validate preset ID if provided
          if (data.data.presetId) {
            const preset = ALL_PRESETS.find(p => p.id === data.data.presetId);
            if (!preset) {
              console.warn(`Invalid preset ID: ${data.data.presetId}`);
              return null;
            }
            return { presetId: data.data.presetId };
          }
          
          // Validate custom session if provided
          if (data.data.customSession) {
            const { frequency, duration, ambientSounds, soundscapes } = data.data.customSession;
            
            // Validate frequency range
            if (frequency <= 0 || frequency > 100) {
              console.warn(`Invalid frequency: ${frequency}`);
              return null;
            }
            
            // Validate duration range
            if (duration < 1 || duration > 720) {
              console.warn(`Invalid duration: ${duration}`);
              return null;
            }
            
            // Validate ambient sounds
            const validAmbientSounds = (ambientSounds || []).filter(sound => 
              ambientSoundPresets.some(preset => preset.id === sound)
            );
            
            // Validate soundscapes
            const validSoundscapes = (soundscapes || []).filter(sound => 
              ambientSoundPresets.some(preset => 
                preset.id === sound && preset.category === 'soundscapes'
              )
            );
            
            return {
              customSession: {
                frequency,
                duration,
                ambientSounds: validAmbientSounds,
                soundscapes: validSoundscapes,
                advancedSettings: data.data.customSession.advancedSettings
              }
            };
          }
        }
      }
    } catch (error) {
      console.error('Error parsing session suggestion:', error);
    }
    return null;
  };

  const validateCustomSession = (session: CustomSession): boolean => {
    // Validate frequency
    if (session.frequency <= 0 || session.frequency > 100) {
      console.error('Invalid frequency:', session.frequency);
      return false;
    }

    // Validate duration (allow up to 12 hours)
    if (session.duration < 1 || session.duration > 720) {
      console.error('Invalid duration:', session.duration);
      return false;
    }

    // Validate ambient sounds
    if (session.ambientSounds && !Array.isArray(session.ambientSounds)) {
      console.error('Invalid ambient sounds:', session.ambientSounds);
      return false;
    }

    // Validate soundscapes
    if (session.soundscapes && !Array.isArray(session.soundscapes)) {
      console.error('Invalid soundscapes:', session.soundscapes);
      return false;
    }

    return true;
  };

  const sendMessage = useCallback(async (content: string) => {
    setIsLoading(true);

    const userMessage: Message = {
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);

    const assistantMessage: Message = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages(prev => [...prev, assistantMessage]);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            { role: 'user', content }
          ],
          model: 'gpt-4-turbo-preview',
          temperature: 0.7,
          max_tokens: 1000,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get chat completion');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      let fullResponse = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Parse the chunk and extract the content
        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n').filter(line => line.trim() !== '');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              break;
            }
            try {
              const parsed = JSON.parse(data);
              if (parsed.choices?.[0]?.delta?.content) {
                const content = parsed.choices[0].delta.content;
                fullResponse += content;
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMessage = newMessages[newMessages.length - 1];
                  if (lastMessage.role === 'assistant') {
                    lastMessage.content = fullResponse;
                    lastMessage.isStreaming = true;
                  }
                  return newMessages;
                });
              }
            } catch (error) {
              console.error('Error parsing chunk:', error);
            }
          }
        }
      }

      // Final update to remove streaming flag
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.role === 'assistant') {
          lastMessage.isStreaming = false;
        }
        return newMessages;
      });

      // Parse and validate any session suggestions
      const suggestion = parseSessionSuggestion(fullResponse);
      if (suggestion) {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage.role === 'assistant') {
            lastMessage.sessionSuggestion = suggestion;
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error('Error in chat:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.role === 'assistant') {
          lastMessage.content = 'Sorry, I encountered an error. Please try again.';
          lastMessage.isStreaming = false;
        }
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    messages,
    sendMessage,
    isLoading
  };
}; 